import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, ExternalLink, Mail, Phone, MapPin, 
  Globe, Facebook, Instagram, Twitter, Youtube, Linkedin,
  Tag, Copy, Check, ChevronRight, Star, Eye,
  Shield, Clock, Heart, DollarSign, Dna, Users, MapPinIcon
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icon mapping for features
const iconMap = {
  Shield, Clock, Heart, DollarSign, Dna, Users, MapPin: MapPinIcon,
  Star, Eye, Tag
};

const PartnerPromoPage = () => {
  const { slug } = useParams();
  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copiedCode, setCopiedCode] = useState(null);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchPartner();
  }, [slug]);

  const fetchPartner = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/partners/${slug}`);
      setPartner(response.data);
    } catch (error) {
      if (error.response?.status === 404) {
        setError('Partner page not found');
      } else {
        setError('Failed to load partner page');
      }
    } finally {
      setLoading(false);
    }
  };

  const copyPromoCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    toast.success('Promo code copied!');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    
    if (!contactForm.name || !contactForm.email || !contactForm.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/partners/${slug}/contact`, {
        partner_id: partner.id,
        ...contactForm
      });
      toast.success('Message sent successfully!');
      setContactForm({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const getFeatureIcon = (iconName) => {
    const Icon = iconMap[iconName] || Star;
    return <Icon className="w-6 h-6" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="py-12 text-center">
            <h2 className="text-xl font-bold mb-2">Page Not Found</h2>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button asChild>
              <Link to="/deals">Browse All Partners</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Banner */}
      <div 
        className="relative"
        style={{ 
          backgroundColor: partner.primary_color || '#1e40af',
          color: '#ffffff'
        }}
      >
        {partner.banner_url && (
          <div 
            className="absolute inset-0 bg-cover bg-center opacity-20"
            style={{ backgroundImage: `url(${partner.banner_url})` }}
          />
        )}
        <div className="relative container mx-auto px-4 py-12">
          <Link 
            to="/deals" 
            className="inline-flex items-center gap-1 text-sm opacity-75 hover:opacity-100 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Partners
          </Link>
          
          <div className="flex flex-col md:flex-row items-start gap-6">
            {/* Logo */}
            {partner.logo_url && (
              <img 
                src={partner.logo_url} 
                alt={partner.name}
                className="w-24 h-24 rounded-xl bg-white p-2 object-contain"
              />
            )}
            
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-3xl md:text-4xl font-bold">{partner.name}</h1>
                {partner.is_featured && (
                  <Badge className="bg-yellow-500 text-yellow-900">
                    <Star className="w-3 h-3 mr-1" /> Featured
                  </Badge>
                )}
              </div>
              {partner.tagline && (
                <p className="text-xl opacity-90">{partner.tagline}</p>
              )}
            </div>

            {/* CTA */}
            {partner.social_links?.website && (
              <Button 
                size="lg" 
                variant="secondary"
                className="whitespace-nowrap"
                onClick={() => window.open(partner.social_links.website, '_blank')}
              >
                Visit Website
                <ExternalLink className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Description */}
            <Card>
              <CardHeader>
                <CardTitle>About {partner.name}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="prose prose-sm max-w-none">
                  {partner.description.split('\n\n').map((paragraph, i) => (
                    <p key={i} className="text-muted-foreground mb-4 last:mb-0">
                      {paragraph}
                    </p>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Features */}
            {partner.features && partner.features.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Why Choose {partner.name}?</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {partner.features.map((feature) => (
                      <div 
                        key={`feature-${feature.title}`}
                        className="flex items-start gap-3 p-4 rounded-lg bg-muted/50"
                      >
                        <div 
                          className="p-2 rounded-lg"
                          style={{ backgroundColor: partner.primary_color || '#1e40af', color: '#fff' }}
                        >
                          {getFeatureIcon(feature.icon)}
                        </div>
                        <div>
                          <h4 className="font-semibold">{feature.title}</h4>
                          <p className="text-sm text-muted-foreground">{feature.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Deals & Offers */}
            {partner.deals && partner.deals.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Tag className="w-5 h-5" />
                    Exclusive Offers
                  </CardTitle>
                  <CardDescription>
                    Special deals for AADC members
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {partner.deals.map((deal, index) => (
                    <div 
                      key={deal.id || index}
                      className="border rounded-lg p-4 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-semibold">{deal.title}</h4>
                            {deal.discount_text && (
                              <Badge 
                                style={{ 
                                  backgroundColor: partner.secondary_color || '#3b82f6',
                                  color: '#fff'
                                }}
                              >
                                {deal.discount_text}
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">{deal.description}</p>
                          {deal.terms && (
                            <p className="text-xs text-muted-foreground mt-2">* {deal.terms}</p>
                          )}
                        </div>
                        
                        {deal.code && (
                          <div className="text-right">
                            <p className="text-xs text-muted-foreground mb-1">Promo Code</p>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => copyPromoCode(deal.code)}
                              className="font-mono"
                            >
                              {deal.code}
                              {copiedCode === deal.code ? (
                                <Check className="w-4 h-4 ml-2 text-green-500" />
                              ) : (
                                <Copy className="w-4 h-4 ml-2" />
                              )}
                            </Button>
                          </div>
                        )}
                      </div>
                      
                      {deal.link_url && (
                        <Button 
                          className="mt-3" 
                          size="sm"
                          onClick={() => window.open(deal.link_url, '_blank')}
                        >
                          Redeem Offer
                          <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Gallery */}
            {partner.gallery_urls && partner.gallery_urls.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Gallery</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {partner.gallery_urls.map((url, index) => (
                      <img 
                        key={`gallery-${index}-${url.slice(-20)}`}
                        src={url}
                        alt={`${partner.name} gallery ${index + 1}`}
                        className="rounded-lg object-cover aspect-video w-full"
                      />
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Contact Info */}
            {partner.contact_info && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Contact Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {partner.contact_info.email && (
                    <a 
                      href={`mailto:${partner.contact_info.email}`}
                      className="flex items-center gap-3 text-sm hover:text-primary"
                    >
                      <Mail className="w-4 h-4 text-muted-foreground" />
                      {partner.contact_info.email}
                    </a>
                  )}
                  {partner.contact_info.phone && (
                    <a 
                      href={`tel:${partner.contact_info.phone}`}
                      className="flex items-center gap-3 text-sm hover:text-primary"
                    >
                      <Phone className="w-4 h-4 text-muted-foreground" />
                      {partner.contact_info.phone}
                    </a>
                  )}
                  {partner.contact_info.address && (
                    <div className="flex items-start gap-3 text-sm">
                      <MapPin className="w-4 h-4 text-muted-foreground mt-0.5" />
                      <span>{partner.contact_info.address}</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Social Links */}
            {partner.social_links && Object.values(partner.social_links).some(v => v) && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Connect</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {partner.social_links.website && (
                      <Button variant="outline" size="sm" asChild>
                        <a href={partner.social_links.website} target="_blank" rel="noopener noreferrer">
                          <Globe className="w-4 h-4 mr-1" /> Website
                        </a>
                      </Button>
                    )}
                    {partner.social_links.facebook && (
                      <Button variant="outline" size="icon" asChild>
                        <a href={partner.social_links.facebook} target="_blank" rel="noopener noreferrer">
                          <Facebook className="w-4 h-4" />
                        </a>
                      </Button>
                    )}
                    {partner.social_links.instagram && (
                      <Button variant="outline" size="icon" asChild>
                        <a href={partner.social_links.instagram} target="_blank" rel="noopener noreferrer">
                          <Instagram className="w-4 h-4" />
                        </a>
                      </Button>
                    )}
                    {partner.social_links.twitter && (
                      <Button variant="outline" size="icon" asChild>
                        <a href={partner.social_links.twitter} target="_blank" rel="noopener noreferrer">
                          <Twitter className="w-4 h-4" />
                        </a>
                      </Button>
                    )}
                    {partner.social_links.youtube && (
                      <Button variant="outline" size="icon" asChild>
                        <a href={partner.social_links.youtube} target="_blank" rel="noopener noreferrer">
                          <Youtube className="w-4 h-4" />
                        </a>
                      </Button>
                    )}
                    {partner.social_links.linkedin && (
                      <Button variant="outline" size="icon" asChild>
                        <a href={partner.social_links.linkedin} target="_blank" rel="noopener noreferrer">
                          <Linkedin className="w-4 h-4" />
                        </a>
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Contact Form */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Send a Message</CardTitle>
                <CardDescription>
                  Have questions? Reach out directly.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleContactSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      value={contactForm.name}
                      onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                      placeholder="Your name"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                      placeholder="your@email.com"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone (optional)</Label>
                    <Input
                      id="phone"
                      value={contactForm.phone}
                      onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                      placeholder="(555) 123-4567"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="message">Message *</Label>
                    <Textarea
                      id="message"
                      value={contactForm.message}
                      onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                      placeholder="How can they help you?"
                      rows={4}
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={submitting}>
                    {submitting ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* View Count */}
            <div className="text-center text-sm text-muted-foreground">
              <Eye className="w-4 h-4 inline mr-1" />
              {partner.view_count.toLocaleString()} views
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartnerPromoPage;
